/**
 * SalarySheetMapping.jsx — Settings > Payroll > Salary Sheet Column Mapping (H5).
 * HR assigns which Salary Master component codes feed each of HR's sheet
 * columns (Basic Pay, HRA, System Allowance, Statutory Bonus). Anything left
 * unassigned automatically becomes "Other Allowance" on the export.
 */
import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { FileSpreadsheet, Save, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

const COLUMNS = [
  { key: 'basic_pay_codes', label: 'Basic Pay' },
  { key: 'hra_codes', label: 'HRA' },
  { key: 'system_allowance_codes', label: 'System Allowance' },
  { key: 'statutory_bonus_codes', label: 'Statutory Bonus' },
];

export default function SalarySheetMapping() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/hr/payroll/salary-sheet-mapping')
      .then(setData)
      .catch(() => toast.error('Failed to load salary sheet mapping'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = (colKey, code) => {
    setData(prev => {
      const cur = prev[colKey] || [];
      const nextList = cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code];
      return { ...prev, [colKey]: nextList };
    });
  };

  const mappedElsewhere = (code, exceptKey) =>
    COLUMNS.some(c => c.key !== exceptKey && (data[c.key] || []).includes(code));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      COLUMNS.forEach(c => { payload[c.key] = data[c.key] || []; });
      const d = await apiPut('/hr/payroll/salary-sheet-mapping', payload);
      toast.success('Salary sheet mapping saved');
      setData({ ...data, ...d });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading || !data) return <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>;

  const otherCodes = (data.available_codes || []).filter(
    code => !COLUMNS.some(c => (data[c.key] || []).includes(code))
  );

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="salary-sheet-mapping-page">
      <PageHeader
        overline="Settings · Payroll"
        title="Salary Sheet Column Mapping"
        subtitle="HR's monthly salary sheet has different column names than the ERP's salary components. Pick which Salary Master component(s) feed each column below — anything left unassigned goes into Other Allowance automatically."
      />
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <FileSpreadsheet size={14} className="text-[#3b4872]" />
          <div className="kpi-overline">Component → Sheet column mapping</div>
        </div>
        {COLUMNS.map(col => (
          <div key={col.key} className="border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-2">{col.label}</label>
            <div className="flex flex-wrap gap-2">
              {(data.available_codes || []).map(code => {
                const selected = (data[col.key] || []).includes(code);
                const disabled = !selected && mappedElsewhere(code, col.key);
                return (
                  <button
                    key={code}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggle(col.key, code)}
                    data-testid={`sheet-mapping-${col.key}-${code}`}
                    title={disabled ? 'Already assigned to another column' : ''}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      selected ? 'bg-[#3b4872] text-white border-[#3b4872]'
                        : disabled ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'
                    }`}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="border-t border-slate-100 pt-4">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-2">Other Allowance (automatic)</label>
          <div className="flex flex-wrap gap-2" data-testid="sheet-mapping-other-allowance">
            {otherCodes.length === 0 ? (
              <span className="text-xs text-slate-400">Every component is mapped above.</span>
            ) : otherCodes.map(code => (
              <span key={code} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-dashed border-slate-300 text-slate-500">{code}</span>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">Everything not assigned above sums into "Other Allowance" on the export — Gross Payable always ties out.</p>
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={save} disabled={saving} data-testid="sheet-mapping-save-btn"
            className="px-5 py-2.5 text-sm bg-[#3b4872] text-white rounded font-display font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Mapping'}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-4">
        Once mapped, go to HR &gt; Payroll &gt; Run for a month and use <strong>Export salary sheet</strong> to download the .xlsx in HR's exact column order.
      </p>
    </div>
  );
}
